export type Cache = {
  /**
   * Gets the value of the given `key`.
   */
  get: <TValue>(
    key: Record<string, any> | string,
    defaultValue: () => Promise<TValue>,
    events?: CacheEvents<TValue> | undefined
  ) => Promise<TValue>;

  /**
   * Sets the given value with the given `key`.
   */
  set: <TValue>(key: Record<string, any> | string, value: TValue) => Promise<TValue>;

  /**
   * Deletes the given `key`.
   */
  delete: (key: Record<string, any> | string) => Promise<void>;

  /**
   * Clears the cache.
   */
  clear: () => Promise<void>;
};

export type CacheEvents<TValue> = {
  /**
   * The callback when the given `key` is missing from the cache.
   */
  miss: (value: TValue) => Promise<any>;
};

export type MemoryCacheOptions = {
  /**
   * If keys and values should be serialized using `JSON.stringify`.
   */
  serializable?: boolean | undefined;
};

export function createMemoryCache(options: MemoryCacheOptions = { serializable: true }): Cache {
  let cache: Record<string, any> = {};

  return {
    get<TValue>(
      key: Record<string, any> | string,
      defaultValue: () => Promise<TValue>,
      events: CacheEvents<TValue> = { miss: (): Promise<void> => Promise.resolve() }
    ): Promise<TValue> {
      const keyAsString = JSON.stringify(key);

      if (keyAsString in cache) {
        return Promise.resolve(options.serializable ? JSON.parse(cache[keyAsString]) : cache[keyAsString]);
      }

      const promise = defaultValue();

      return promise.then((value: TValue) => events.miss(value)).then(() => promise);
    },

    set<TValue>(key: Record<string, any> | string, value: TValue): Promise<TValue> {
      cache[JSON.stringify(key)] = options.serializable ? JSON.stringify(value) : value;

      return Promise.resolve(value);
    },

    delete(key: Record<string, unknown> | string): Promise<void> {
      delete cache[JSON.stringify(key)];

      return Promise.resolve();
    },

    clear(): Promise<void> {
      cache = {};

      return Promise.resolve();
    },
  };
}

export function createNullCache() {
  return {
    get<TValue>(
      _key: Record<string, any> | string,
      defaultValue: () => Promise<TValue>,
      events = { miss: (): Promise<void> => Promise.resolve() }
    ): Promise<TValue> {
      const value = defaultValue();

      return value.then((result) => Promise.all([result, events.miss()])).then(([result]) => result);
    },

    set<TValue>(_key: Record<string, any> | string, value: TValue): Promise<TValue> {
      return Promise.resolve(value);
    },

    delete(_key: Record<string, any> | string): Promise<void> {
      return Promise.resolve();
    },

    clear(): Promise<void> {
      return Promise.resolve();
    },
  };
}
